/**
 * The user's activity feed entries, server-paged, with delete. These are the "did X" lines friends
 * see; deleting one removes the entry, not the thing it describes.
 */

import { useState } from 'react'
import { Chip, Typography } from '@mui/material'
import { Delete, DynamicFeed } from '@mui/icons-material'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { userDataApi } from '@api/endpoints/userData'
import type { AdminUserActivityDto } from '@api/types/userData'
import { UserDataActionDialog } from './UserDataActionDialog'
import { UserDataChangeResultDialog } from './UserDataChangeResultDialog'
import { UserDataSection } from './UserDataSection'
import { adminUserActivitiesKey } from './userQueryKeys'
import { errorMessage, formatDate } from './userFormatting'
import { useUserDataChange } from './useUserDataChange'
import { useIsAdmin } from './useIsAdmin'

export function UserActivityFeedSection({ userId }: { userId: string }) {
  const change = useUserDataChange(userId)
  // Moderators see this read-only; every write here is Admin-only on the API too.
  const isAdmin = useIsAdmin()
  const [target, setTarget] = useState<AdminUserActivityDto | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { data, isLoading, error } = useQuery({
    queryKey: [...adminUserActivitiesKey(userId), page, pageSize],
    // The endpoint pages from 1; DataTable from 0.
    queryFn: () => userDataApi.getActivities(userId, page + 1, pageSize),
    placeholderData: keepPreviousData,
  })

  // Deleting the last entry on the last page would otherwise leave the table on an empty page.
  const lastPage = data ? Math.max(0, Math.ceil(data.totalCount / pageSize) - 1) : 0
  if (data && page > lastPage) setPage(lastPage)

  const close = () => {
    setTarget(null)
    change.resetError()
  }

  const columns: DataTableColumn<AdminUserActivityDto>[] = [
    { key: 'when', header: 'When', primary: true, render: (a) => formatDate(a.timestamp) },
    { key: 'type', header: 'Activity', render: (a) => <Chip size="small" variant="outlined" label={a.activityType} /> },
    { key: 'target', header: 'About', render: (a) => a.adventureTitle || a.targetType },
    {
      key: 'notes',
      header: 'Notes',
      hideBelow: 'md',
      render: (a) =>
        a.notes ? (
          <Typography
            variant="body2"
            component="span"
            title={a.notes}
            sx={{ display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {a.notes}
          </Typography>
        ) : (
          '—'
        ),
    },
    { key: 'points', header: 'Points', align: 'right', render: (a) => a.pointsEarned.toLocaleString() },
  ]

  return (
    <UserDataSection title="Activity feed" icon={<DynamicFeed fontSize="small" />}>
      <DataTable
        rows={data?.items ?? []}
        columns={columns}
        getRowKey={(a) => a.id}
        loading={isLoading}
        error={error ? errorMessage(error, 'Failed to load the activity feed') : null}
        emptyMessage="No activity"
        dense
        page={page}
        pageSize={pageSize}
        total={data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowActions={(a) =>
          isAdmin
            ? [{ key: 'delete', label: 'Delete activity', icon: <Delete fontSize="small" />, color: 'error', onClick: () => setTarget(a) }]
            : []
        }
      />

      {target && (
        <UserDataActionDialog
          title="Delete activity entry?"
          message={`${target.activityType}${target.adventureTitle ? ` — ${target.adventureTitle}` : ''}, ${formatDate(target.timestamp)}. Removes the feed entry only. This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          pending={change.pending}
          error={change.error}
          onSubmit={(reason) => change.submit(() => userDataApi.deleteActivity(userId, target.id, reason), close)}
          onClose={close}
        />
      )}

      <UserDataChangeResultDialog open={change.resultOpen} result={change.result} onClose={change.closeResult} />
    </UserDataSection>
  )
}
